<template>
  <div class="payment-history">
    <PaymentProofModal
      v-if="showPaymentProof"
      :summary-data="selectedPayment"
      @close="handleCloseModal"
    />

    <PaymentRequestModal
      v-if="showPaymentRequestModal"
      :payment="selectedPayment"
      @close="handleCloseModal"
    />

    <div
      v-if="paymentsByStatus.Pending > 0"
      class="payment-history__header"
    >
      <WarningPendentCount
        :pendent-warning-count="paymentsByStatus.Pending"
        :pending-types="getPendingPaymentTypes()"
      />
    </div>

    <div class="payment-history__actions">
      <ul class="payment-history__filters">
        <li
          :class="['filter', { '--active': selectedFilter === 'all' }]"
          @click="setFilter('all')"
        >
          <button class="filter--all">
            <svg-loader src="/static/images/group-menu.svg" />
          </button>
          {{ paymentsList.length }} Todos
        </li>
        <li
          :class="['filter', { '--active': selectedFilter === 'Pending' }]"
          @click="setFilter('Pending')"
        >
          <button class="filter--pending">
            <svg-loader src="/static/images/icon-dashboard-calendar-white.svg" />
          </button>
          {{ paymentsByStatus.Pending || 0 }} Pendente(s)
        </li>
        <li
          :class="['filter', { '--active': selectedFilter === 'PaymentConfirmed' }]"
          @click="setFilter('PaymentConfirmed')"
        >
          <button class="filter--paid">
            <svg-loader src="/static/images/icon-check-thin.svg" />
          </button>
          {{ paymentsByStatus.PaymentConfirmed || 0 }} Pago(s)
        </li>
        <li
          :class="['filter', { '--active': selectedFilter === 'Expired' }]"
          @click="setFilter('Expired')"
        >
          <button class="filter--expired">
            <svg-loader src="/static/images/status-info-white.svg" />
          </button>
          {{ paymentsByStatus.Expired || 0 }} Expirado(s)
        </li>
      </ul>

      <div class="payment-history__search">
        <SearchFieldSelector
          :default-start-date="defaultStartDate"
          :default-end-date="defaultEndDate"
          :options="dateSelectOptions"
          :clean-date="cleanDate"
          @updateSearchValue="handleDateRangeUpdate"
        />

        <button
          class="md-button btn btn-primary btn-search"
          @click="getPaymentHistory(searchPeriod)"
        >
          <md-icon
            md-src="/static/images/icon-search.svg"
            class="icon-search"
          >
            search
          </md-icon>
          Buscar
        </button>
      </div>
    </div>

    <div class="payment-history__cards">
      <div
        v-if="isLoadingPayments"
        class="payment-history__loading"
      >
        <Spinner />
      </div>
      <template v-else>
        <PaymentStatusCard
          v-for="payment in filteredPaymentCards"
          :key="payment.paymentId"
          :payment-method="payment?.paymentMethod?.type"
          :operation-code="payment.transactionId"
          :date="payment.createdAt"
          :amount="payment.amount"
          :status="payment.status"
          @click.native="openPaymentModal(payment)"
        />
      </template>
    </div>
  </div>
</template>

<script>
import Spinner from '../../common/Spinner';
import WarningPendentCount from '../../common/WarningPendentCount';
import PaymentStatusCard from './PaymentStatusCard';
import SearchFieldSelector from '../../common/SearchFieldSelector';
import PaymentProofModal from './PaymentProofModal';
import PaymentRequestModal from './PaymentRequestModal';

import Moment from 'moment';
import notify from '../../../api/notification';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'PaymentHistory',
  components: {
    Spinner,
    WarningPendentCount,
    PaymentStatusCard,
    SearchFieldSelector,
    PaymentProofModal,
    PaymentRequestModal
  },
  data() {
    const todayStr = Moment().format('YYYY-MM-DD');
    const startDateStr = Moment().subtract(30, 'days').format('YYYY-MM-DD');

    return {
      selectedFilter: 'all',
      dateSelectOptions: [
        {
          value: 'date',
          label: 'Data de Solicitação',
          placeholder: 'Data de Solicitação',
          limitDays: 1827,
          singleDate: false,
          selected: true,
          type: 'date'
        }
      ],
      selectedPayment: null,
      showPaymentProof: false,
      showPaymentRequestModal: false,
      searchPeriod: {
        startDate: startDateStr,
        endDate: todayStr
      },
      cleanDate: 0,
    };
  },
  computed: {
    ...mapGetters(['paymentsList', 'isLoadingPayments', 'paymentsByStatus']),
    filteredPaymentCards() {
      if (this.selectedFilter === 'all') return this.paymentsList;
      return this.paymentsList.filter(card => card.status === this.selectedFilter);
    },
    defaultStartDate() {
      return Moment(this.searchPeriod.startDate, 'YYYY-MM-DD').format('DD/MM/YYYY');
    },
    defaultEndDate() {
      return Moment(this.searchPeriod.endDate, 'YYYY-MM-DD').format('DD/MM/YYYY');
    }
  },
  mounted() {
    this.fetchPayments();
  },
  methods: {
    ...mapActions(['getPaymentHistory']),
    fetchPayments() {
      this.getPaymentHistory(this.searchPeriod);
    },
    setFilter(filter) {
      this.selectedFilter = filter;
      this.fetchPayments();
    },
    handleDateRangeUpdate(value) {
      if (value.type === 'date') {
        if (this.isRangeValid(value.date)) {
          this.searchPeriod.startDate = Moment(value.date.start).format('YYYY-MM-DD');
          this.searchPeriod.endDate = Moment(value.date.end).format('YYYY-MM-DD');
        } else {
          this.cleanDate++;
          this.searchPeriod.startDate = Moment().format('YYYY-MM-DD');
          this.searchPeriod.endDate = Moment().format('YYYY-MM-DD');

          notify({
            title: 'AVISO:',
            message: 'Selecione um período de até 30 dias.'
          });
        }
      }
    },
    isRangeValid(date) {
      const startDate = Moment(date.start);
      const endDate = Moment(date.end);
      const diffInDays = endDate.diff(startDate, 'days');

      return diffInDays <= 30;
    },
    openPaymentModal(payment) {
      this.selectedPayment = payment;
      if(payment.status === 'PaymentConfirmed'){
        this.showPaymentProof = true;
      } else {
        this.showPaymentRequestModal = true;
      }
    },
    handleCloseModal() {
      this.showPaymentProof = false;
      this.showPaymentRequestModal = false;
      this.fetchPayments();
    },
    getPendingPaymentTypes() {
      const types = new Set();
      
      this.paymentsList.forEach(payment => {
        if (payment.status === 'Pending') {
          if (payment.paymentMethod.type === 'PIX') {
            types.add('pix');
          } else if (payment.paymentMethod.type === 'BOLETO') {
            types.add('boletos');
          }
        }
      });
      
      return Array.from(types);
    }
  }
};
</script>

<style lang="scss" scoped>
.payment-history {
  &__header {
    display: flex;
    justify-content: flex-end;
  }

  &__actions {
    display: flex;
    align-items: center;
    margin-top: 24px;
    justify-content: space-between;
  }

  &__filters {
    display: flex;
    gap: 24px;

    .filter {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin: 0;
      gap: 12px;
      padding: 8px;
      cursor: pointer;
      background-color: transparent;
      transition: all ease-in-out 0.3s;

      &.--active {
        background-color: #fff;
        border-radius: 4px;
        box-shadow: 0px 2px 2px 0px #1616160a;
      }

      button {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .md-icon {
        min-width: 14px;
        min-height: 14px;
        width: 14px;
        height: 14px;
      }

      &--all {
        background-color: #919191;
      }

      &--pending {
        background-color: #f78e26;
      }

      &--paid {
        background-color: #32a454;
      }

      &--expired {
        background-color: #bb230c;
      }
    }
  }

  &__search {
    display: flex;
    gap: 15px;
    align-items: center;

    .search {
      margin-top: 0;
      flex: 1;
    }

    .btn-search {
      height: 50px;
      width: 128px;

      .md-icon {
        margin-right: 6px;
      }
    }
  }

  &__loading {
    height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
  }

  &__cards {
    display: flex;
    gap: 16px;
    margin-top: 24px;
    flex-wrap: wrap;
  }
}
</style>
